import { AxiosResponse } from 'axios'

import { axiosInstance as axios } from 'utils/requester'
import { getLanguageCode } from 'utils/translation'
import { API_BASE_URL } from 'utils/consts.mjs'
import { text2sha } from 'utils/userAuth'

interface CustomAxiosResponse<T> extends AxiosResponse {
  data: T
  error?: string
}

export interface CheckoutErrorResponse {
  data: {
    code: string
    error: string
    email: string
    description: {
      error: string
      three_d_secure_action_token_id?: string
    }
  }
  request: {
    responseURL: string
  }
}

export interface CheckoutError extends Error {
  code: string
  message: string
  name: string
  status: number
  response: CheckoutErrorResponse
}

export interface PaypalPayment {
  type: 'paypal'
  id: string
  payer_id: string
  email: string
}

export interface RecurlyCard {
  brand: string
  first_six: string
  last_four: string
  exp_month: number
  exp_year: number
  issuing_country: string
  funding_source: string
}

export interface RecurlyPayment {
  type: string
  id: string
  card: RecurlyCard
}

export type PaymentMethod = RecurlyPayment | PaypalPayment

export interface CheckoutParams {
  currency: string
  noTrial: boolean
  paypal_account_hash: string | null
  plan: string
  subscriptionType: string
  item?: string
  addOn?: string
  coupon?: string
  threeDSecureToken?: string
  eventId?: string
}

export interface CheckoutResponse {
  success: boolean
  transactionId?: string
  error: string
}

export const checkout = (
  params: CheckoutParams,
): Promise<CustomAxiosResponse<CheckoutResponse>> => {
  const { addOn, noTrial, eventId, ...rest } = params
  return axios({
    method: 'get',
    baseURL: API_BASE_URL,
    url: 'checkout',
    withCredentials: true,
    params: {
      ...rest,
      add_on: addOn,
      no_trial: noTrial,
      event_id: eventId,
      preferred_locale: getLanguageCode(),
    },
  })
}

export interface PreCheckoutParams {
  currency: string
  paymentMethod: string
  threeDSecureToken?: string
  eventId?: string
}

export interface PreCheckoutResponse {
  success: boolean
  error: string
}

export const preCheckout = (
  params: PreCheckoutParams,
): Promise<CustomAxiosResponse<PreCheckoutResponse>> => {
  const { eventId, ...rest } = params
  return axios({
    method: 'get',
    baseURL: API_BASE_URL,
    url: 'pre_checkout',
    withCredentials: true,
    params: {
      ...rest,
      event_id: eventId,
      preferred_locale: getLanguageCode(),
    },
  })
}

export interface ProcessCheckoutParams {
  paymentMethod: PaymentMethod
  noTrial?: boolean
  plan: string
  currency?: string
  skipPreCheckout: boolean
  subscriptionType?: string
  threeDSecureToken?: string
  addOn?: string
  coupon?: string
  item?: string
  eventId?: string
}

const isPaypalPayment = (method: PaymentMethod): method is PaypalPayment => {
  return method.type === 'paypal'
}

export const processCheckout = async (
  params: ProcessCheckoutParams,
): Promise<CustomAxiosResponse<CheckoutResponse | PreCheckoutResponse>> => {
  const {
    paymentMethod,
    plan,
    subscriptionType = 'recurly',
    currency = 'USD',
    threeDSecureToken,
    coupon,
    addOn,
    noTrial = false,
    item,
    skipPreCheckout = false,
    eventId,
  } = params

  const preCheckoutResult = skipPreCheckout
    ? null
    : await preCheckout({
        paymentMethod: paymentMethod.id,
        currency,
        threeDSecureToken,
        eventId,
      })

  const paypal_account_hash = isPaypalPayment(paymentMethod)
    ? await text2sha({
        algorithm: 'SHA-1',
        text: `${paymentMethod.email}:${paymentMethod.payer_id}`,
      })
    : null

  const checkoutResult = await checkout({
    plan,
    subscriptionType,
    currency,
    coupon,
    addOn,
    noTrial,
    item,
    paypal_account_hash,
    threeDSecureToken: skipPreCheckout ? threeDSecureToken : undefined,
    eventId,
  })

  return preCheckoutResult || checkoutResult
}
